import type { State } from '@contracts/order/valueNormalizers/State';
import { yupResolver } from '@hookform/resolvers/yup';
import { CompanyInformationForm } from '@src/clients/CompanyInformationForm';
import type { Client } from '@src/clients/domain/Client';
import { useClientUpdate } from '@src/clients/hooks/useClientUpdate';
import {
  AdminClientFormSchema,
  IsAgencyField,
} from '@src/clients/schemas/AdminClientFormSchema';
import type { ClientFormSchema } from '@src/clients/schemas/ClientFormSchema';
import { mapJurisdictionIdNumbersToClientForm } from '@src/clients/schemas/ClientFormSchema';
import { BaseModal } from '@src/common/components/BaseModal';
import { Button } from '@src/common/components/button/Button';
import { CancelButton } from '@src/common/components/button/CancelButton';
import { SaveChangesButton } from '@src/common/components/button/SaveChangesButton';
import { Divider } from '@src/common/components/Divider';
import { Stack } from '@src/common/components/Stack';
import { ToastLevel } from '@src/common/components/ToastLevel';
import { Typography } from '@src/common/components/Typography';
import { useToast } from '@src/common/hooks/useToast';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { ControllerFormCheckbox } from '@src/orders/PermitFormSections/FormCheckbox';
import { useEffect, useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { type Control, type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type * as yup from 'yup';

interface Props<T extends FieldValues> {
  client: Client;
  stayOnPage?: boolean;
  schema?: yup.ObjectSchema<T>;
  AdditionalDetails?: (props: { control: Control<T> }) => JSX.Element;
}

export const AdminCompanyInformation = ({ client }: { client: Client }) => (
  <CompanyInformation
    client={client}
    AdditionalDetails={({
      control,
    }: {
      control: Control<yup.InferType<typeof AdminClientFormSchema>>;
    }) => (
      <Stack direction="row" gap={theme.spacing['6']}>
        <ControllerFormCheckbox
          control={control}
          fieldName={IsAgencyField.fieldName}
        />
      </Stack>
    )}
  />
);

export const CompanyInformation = ({
  client,
  stayOnPage,
  AdditionalDetails,
}: Props<yup.InferType<typeof AdminClientFormSchema>>) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { showToast } = useToast();
  const [mailingIsSameAsPhysical, setMailingIsSameAsPhysical] = useState(
    client.hasSamePhysicalAndMailingAddress(),
  );

  const {
    control,
    handleSubmit,
    unregister,
    formState: { dirtyFields },
  } = useForm({
    resolver: yupResolver(AdminClientFormSchema),
    defaultValues: {
      name: client.name,
      doingBusinessAs: client.doingBusinessAs,
      fax: client.fax,
      communicationLanguage: client.communicationLanguage,
      address: { ...client.address, state: client.address.state as State },
      mailingAddress: {
        ...client.mailingAddress,
        state: client.mailingAddress.state as State,
      },
      primaryContact: client.primaryContact,
      primaryDeliveryType: {
        primaryDeliveryType: client.primaryDeliveryType.deliveryType,
        value: client.primaryDeliveryType.value,
      },
      secondaryDeliveryType: {
        secondaryDeliveryType: client.secondaryDeliveryType?.deliveryType,
        value: client.secondaryDeliveryType?.value,
      },
      permitNotifications: client.permitNotifications,
      insuranceInformation: client.insuranceInformation,
      jurisdictionIdNumbers: mapJurisdictionIdNumbersToClientForm(
        client.jurisdictionIdNumbers,
      ),
      isAgency: client.isAgency(),
    },
    context: {
      isMailingAddressSameAsPhysical: mailingIsSameAsPhysical,
    },
  });

  useEffect(() => {
    if (mailingIsSameAsPhysical) {
      unregister('mailingAddress');
    }
  }, [unregister, mailingIsSameAsPhysical]);

  const navigate = useNavigate();
  const navigateToClients = () => !stayOnPage && navigate('/clients');
  const closeModal = () => setIsModalOpen(false);
  const isFormDirty = () => Object.keys(dirtyFields).length > 0;

  const onCancelClick = () => {
    if (isFormDirty()) {
      setIsModalOpen(true);
    } else {
      navigateToClients();
    }
  };

  const { updateClient, isLoading } = useClientUpdate({
    client,
    onSuccess: () => {
      navigateToClients();
      showToast({
        severity: ToastLevel.success,
        message: t('changesSaved'),
      });
    },
  });

  const submitClient: SubmitHandler<yup.InferType<typeof ClientFormSchema>> = (
    data,
  ) => {
    updateClient(data);
  };

  return (
    <form onSubmit={handleSubmit(submitClient)}>
      <Stack direction="column">
        <CompanyInformationForm
          control={control}
          onMailingIsSameAsPhysicalChange={setMailingIsSameAsPhysical}
          mailingIsSameAsPhysical={mailingIsSameAsPhysical}
          AdditionalDetails={AdditionalDetails}
        />

        <Stack direction="row" justifyContent="right" gap={2}>
          {!stayOnPage && <CancelButton onClick={onCancelClick} />}

          <SaveChangesButton disabled={isLoading} />
        </Stack>
      </Stack>

      <BaseModal
        title={t('unsavedChangesModal.title')}
        open={isModalOpen}
        onClose={closeModal}
      >
        <Typography className={modalDescription} variant="body2">
          {t('unsavedChangesModal.description')}
        </Typography>

        <Divider />

        <Stack
          marginTop={theme.spacing['6']}
          direction="row"
          justifyContent="right"
          gap={2}
        >
          <Button variant="outlined" onClick={navigateToClients}>
            {t('discardChanges')}
          </Button>

          <SaveChangesButton
            disabled={isLoading}
            onClick={handleSubmit(submitClient)}
          />
        </Stack>
      </BaseModal>
    </form>
  );
};

const modalDescription = css((theme) => ({
  marginTop: theme.spacing['4'],
  marginBottom: theme.spacing['6'],
  width: '560px',
}));
